import showdown from 'showdown';
import { ChildMethod, MethodType, OrderedObject } from '@/templates/NewRoutes';
import { store } from '@/main';
import { Breadcrumbs, LanguagesPack } from '@/store/types';
import { TutorialType } from '@/templates/Tutorial';
import { RootTree, TreeData } from '@/templates/Tree';

import { convChar, convertToAnchorIdHelper, parseHrefHelper } from '@/heplers/stringHelpers';
import flatten from 'lodash.flatten';
import { LocaleItem } from '@/templates/DataNames';

interface Route {
  fqdn: string;
  title: string | { [key: string]: string };
  url: string;
}

export interface ObserverParameters {
  selectors?: HTMLElement[] | any;
  options?: { threshold: number; rootMargin?: string };
  resultFunction?: (entry: any) => void;
  context?: any;
}

export const currentInstance = process.env.VUE_APP_INSTANCE === 'vox' ? 'vox' : 'kit';

export const uppercaseKinds: any = {
  enum: 'Enums',
  module: 'Modules',
  method: 'Methods',
  class: 'Classes',
  howto: 'Howtos',
  interface: 'Interfaces',
  namespace: 'Namespaces',
  protocol: 'Protocols',
  constants: 'Constants',
  ref_folder: 'Ref folders',
  folder: 'Folders',
  root: 'Roots',
  tutorial: 'Tutorials',
  changelog: 'Changelog',
  prop: 'Props',
  member: 'Members',
  events: 'Events',
  constructor: 'Constructors',
  error_list: 'Error lists',
  typedef: 'Typedefs',
  function: 'Functions',
  var: 'Vars',
  const: 'Consts',
  getter: 'Getters',
  setter: 'Setters',
  api_method: 'Methods',
  api_struct: 'Structures',
  api_field: 'Fields',
  object: 'Object',
  error_class: 'Errors',
};

export const anchorKinds = [
  'prop',
  'member',
  'method',
  'const',
  'getter',
  'setter',
  'constructor',
  'constants',
  'api_method',
  'api_field',
];

export const localeList: LocaleItem[] = [
  { title: 'Russian', icon: 'locale__russian', lc: 'ru' },
  { title: 'English', icon: 'locale__english', lc: 'en' },
  { title: 'Spanish', icon: 'locale__spanish', lc: 'es' },
  { title: 'Portuguese', icon: 'locale__portuguese', lc: 'pt' },
];

export const languagesPack: LanguagesPack = {
  http_api: {
    curl: 'true',
    go: 'false',
    nodejs: 'true',
    php: 'true',
    python: 'true',
    java: 'true',
    csharp: 'true',
  },
  avatarapi: {
    grpc: 'true',
  },
  avatarengine: {
    javascript: 'true',
  },
  imapi: {
    grpc: 'true',
  },
  voxengine: {
    javascript: 'true',
  },
  html: {
    html: 'true',
  },
  javascript: {
    javascript: 'true',
  },
  websdk: {
    javascript: 'true',
  },
  ['websdk-v5']: {
    javascript: 'true',
  },
  androidsdk: {
    java: 'true',
    kotlin: 'true',
  },
  android: {
    kotlin: 'true',
  },
  androidsdk3: {
    kotlin: 'true',
  },
  iossdk: {
    objc: 'true',
    swift: 'true',
  },
  unitysdk: {
    csharp: 'true',
  },
  reactnative: {
    javascript: 'true',
  },
  ['reactnative-v2']: {
    javascript: 'true',
  },
  fluttersdk: {
    dart: 'true',
  },
  kit: {
    curl: 'true',
  },
  kit_functions: {
    javascript: 'true',
  },
};

export const languageArray = [
  'curl',
  'go',
  'nodejs',
  'php',
  'python',
  'javascript',
  'html',
  'csharp',
  'java',
  'kotlin',
  'objc',
  'curl',
  'dart',
];

function generateRandomTitle(route: Route) {
  return Object.values(route.title).filter((value) => value.length > 0)[0];
}

/**
 *  Разворачиваем дерево в одномерный массив обьектов для дальнейшего превращения его в роуты
 *  Мы редьюсим массив значений дерева и пушим текущее значение как обьект в итоговый массив. Таким образом проходимся по всему дереву смотря на наличие children
 *
 * @param response дерево документации
 * @return Array из обьектов состоящее из title, url для роута и fqdn.
 */
export function expandTree(response: RootTree) {
  const reducer = (acc: any, currentValue: any) => [
    ...acc,
    {
      title: currentValue.title,
      url: parseHrefHelper(currentValue.fqdn),
      fqdn: currentValue.fqdn,
    },
    ...(currentValue.children ? currentValue.children.reduce(reducer, []) : []),
  ];
  return Object.values(response).reduce(reducer, []);
}

export function isEmptyObject(obj: any): boolean {
  if (!obj) return true;

  if (typeof obj !== 'object') return true;

  return !flatten(Object.values(obj)).length;
}

export function unobserveHelper(params: ObserverParameters) {
  const observer: IntersectionObserver = new IntersectionObserver(
    params.resultFunction,
    params.options
  );

  params.selectors.forEach((v: Element) => {
    observer.unobserve(v);
  });
}

// @deprecated Use [createIntersectionObserver](src/heplers/intersectionObserver.ts) instead
export function createObserverHelper(params: ObserverParameters) {
  const observer: IntersectionObserver = new IntersectionObserver(
    (entries: IntersectionObserverEntry[]) => {
      findAnchors(entries, params.resultFunction);
    },
    params.options
  );

  params.selectors.forEach((v: Element) => {
    observer.observe(v);
  });

  return () => {
    params.selectors.forEach((v: Element) => {
      observer.unobserve(v);
    });
  };
}

function findAnchors(
  entries: IntersectionObserverEntry[],
  resultFunction: ObserverParameters['resultFunction']
) {
  for (const entry of entries) {
    resultFunction(entry);
  }
}

export function debounce(callback: (...args: any[]) => unknown, wait: number, context?: any) {
  let timeout: number;
  return (...args: any) => {
    clearTimeout(timeout);
    timeout = window.setTimeout(() => callback.apply(context, args), wait);
  };
}

export function generateRoutes(data: any, component: any) {
  return expandTree(data).map((route: Route) => {
    return {
      path: parseHrefHelper(route.fqdn, '/docs/'),
      meta: {
        fqdn: `${route.fqdn}`,
      },
      name:
        (typeof route.title === 'object' ? generateRandomTitle(route) : route.title) + route.fqdn,
      component: component,
    };
  });
}

/**
 *  Создаем объект бредкрамбов из дерева документации и присланного fqdn
 *  Мы редьюсим массив значений дерева, и если есть совпадение по текущему fqdn и тому что прислали, то пушим обьект в массив. Таким образом проходимся по всему дереву смотря на наличие children
 * @param response дерево документации
 * @param breadcrumb - fqdn по которому мы должны сделать путь к нему
 * @return Array бредкрамбов состоящий из обьектов с title и url.
 */
export function createBreadcrumbs(response: MethodType, breadcrumb: string): Breadcrumbs[] {
  const reduceObject = (objAcc: Breadcrumbs[], objValue: any) => {
    let isEquals = false;

    // Режем текущий fqdn до длины fqdn дерева, и проверяем их на схожесть
    if (breadcrumb.includes(objValue.fqdn)) {
      const splitFqdn = objValue.fqdn.split('.');
      const splitBreadcrumbs = breadcrumb.split('.').slice(0, splitFqdn.length);

      if (splitFqdn.join('.') === splitBreadcrumbs.join('.')) isEquals = true;
    }

    return isEquals
      ? [
          ...objAcc,
          {
            title: parseLanguageHelper(objValue.title),
            url: parseHrefHelper(objValue.fqdn, '/docs/'),
          },
          ...(objValue.children ? objValue.children.reduce(reduceObject, []) : []),
        ]
      : objAcc;
  };

  return Object.values(response).reduce(reduceObject, []);
}

export function cropTree(tree: TreeData[]): any {
  function recursive(treeData: TreeData): any {
    if (treeData.children) {
      treeData.children = treeData.children.map((child: TreeData) => recursive(child));
    }

    return {
      fqdn: treeData.fqdn,
      kind: treeData.kind,
      title: treeData.title,
      ...(treeData.children ? { children: treeData.children } : {}),
    };
  }

  return tree.map((rootTree: TreeData) => recursive(rootTree));
}

function createExternalLink(mdLinkString: string) {
  if (!mdLinkString.includes('http') || mdLinkString.includes(document.location.origin)) {
    return mdLinkString;
  }

  const linkMatch = mdLinkString.match(/(?<=\])\(\S*?\)$/);
  const textMatch = mdLinkString.match(/^\[.*?\](?=\()/);

  const link = linkMatch?.[0].slice(1, -1);
  const text = textMatch?.[0].slice(1, -1);

  return `<a href="${link}" target="_blank">${text}</a>`;
}

export function convertToHtmlHelper(mdString: string) {
  if (!mdString || !mdString.length) return '';

  /* Regex to find Markdown links, images, and text in square brackets
   * Examples: [text](url), ![alt](url), [just text]
   * !?          — optional '!' at the start, indicates an image (![alt](url))
   * \[.*?]      — captures the text inside square brackets (link text or image alt)
   * (?: ... )?  — optional non-capturing group for URL inside parentheses
   *   \(        — opening parenthesis for the URL
   *   (?:       — non-capturing group for URL content
   *     [^()\\] — any character except parentheses and backslash
   *     |       — or
   *     \(.*?\) — nested parentheses inside URL (only one level)
   *   )*        — repeat multiple times
   *   \)        — closing parenthesis for the URL
   */
  const mdLinks = mdString.match(/(!?\[.*?])(?:\(((?:[^()\\]|\(.*?\))*)\))?/g);

  if (mdLinks && typeof window !== 'undefined') {
    mdLinks.forEach((mdLinkString) => {
      // for external links separate transformation to open them in a new tab
      const newMatch = createExternalLink(mdLinkString);
      if (mdLinkString !== newMatch) {
        mdString = mdString.replace(mdLinkString, newMatch);
      }
    });

    const urls = Array.from(document.querySelectorAll('[class*=vox-multilink]'));
    const search = store.state.selectedLang == 'javascript' ? 'js' : store.state.selectedLang;

    urls.forEach((i) => {
      //Удаляем все текущие активные классы
      i.classList.remove('vox-multilink--active');

      //Проверяем на наличие слова default, делаем активным
      i.className.match(/^.*vox-(.+)--default.*$/) ? i.classList.add('vox-multilink--active') : '';
      //Проверяем на наличие слова выбранного языка, делаем активным
      i.className.match(`vox-(.+)-${search}`) ? i.classList.add('vox-multilink--active') : '';
    });
  }

  // То что прилетело может быть обьектом, протаскиваем через конвертер по языку
  const converter = new showdown.Converter({
    ghCodeBlocks: true,
    literalMidWordUnderscores: true,
    underline: true,
    tables: true,
  });

  return converter.makeHtml(mdString);
}

export function convertCharAndConvertToHtml(mdString: string) {
  // То что прилетело может быть обьектом, протаскиваем через конвертер по языку
  let parseString = mdString;
  const converter = new showdown.Converter({
    ghCodeBlocks: true,
    literalMidWordUnderscores: true,
    underline: true,
    tables: true,
  });

  // Конвертируем символы <, &, > в HTML Encode
  parseString = convChar(parseString);

  return converter.makeHtml(parseString);
}

/**
 *  Вытаскиваем выбранное sdk по методу совпадения с fqdn текущего роута
 *
 * @param route - fqdn текущего роута
 * @return string текущее sdk
 */
export function getSelectedLangName(route: string): string {
  if (!route) return route;

  let sdk: string = route.split('.')[1];

  if (sdk === 'httpapi') sdk = 'http_api';
  if (Object.keys(languagesPack).includes(sdk)) return sdk;
}

export function removeReact<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

export function parseDataWithCurrentLang(data: any) {
  function transformLang(obj: any): any {
    if (obj === null) return null;

    if (typeof obj !== 'object') return obj;

    const newO: any = {};
    // Итерируем по Массиву
    if (Array.isArray(obj))
      if (obj.length) return obj.map((elm: any) => transformLang(elm));
      else return null;
    else {
      if (checkForLang(Object.keys(obj))) return parseLanguageHelper(obj);
    }
    Object.keys(obj).forEach((elm) => (newO[elm] = transformLang(obj[elm])));
    return newO;
  }

  return transformLang(data);
}

export function addUniqExamplesId(data: any) {
  if (data === null) return null;

  if (typeof data !== 'object') return data;

  if (data.content) {
    return {
      ...data,
      ['content']: parseBlogExamples(data.content),
    };
  }
  if (data.children) {
    return {
      ...data,
      ['children']: parseReferenceExamples(data.children),
    };
  }

  return data;
}

function parseBlogExamples(contentArr: any) {
  let counter = 0;
  return contentArr.map((obj: any) => {
    if (!obj) return obj;

    if (obj.kind === 'content_source' && obj.title)
      return {
        ...obj,
        uniqId: convertToAnchorIdHelper(obj.title + '_' + counter++),
      };
    else return obj;
  });
}

function parseReferenceExamples(contentArr: any) {
  let counter = 0;
  return contentArr.map((obj: any) => {
    if (obj.examples && obj.examples.title)
      return {
        ...obj,
        uniqId: convertToAnchorIdHelper(obj.examples.title + '_' + counter++),
      };
    else return obj;
  });
}

function checkForLang(value: string[]) {
  return value.find((objKey: string) => languageArray.includes(objKey));
}

/**
 * По текущему языку вытаскиваем результат
 *
 * @param field поле которое может быть или строкой, или обьектом со значениями для разных языков. Пример { objc: 'AAAA', swift: 'BBBB' }
 * @return string или значение по выбранному языку, или если пришла строка, то ее же
 */
export function parseLanguageHelper(field: string | { [k: string]: string }): string {
  const currentLanguage = store.state.selectedLang;
  if (typeof field !== 'object') return field;

  if (Array.isArray(field) && !field.length) return null;

  const currentLanguagesPack = store.getters.getSelectedLanguagesPack;

  let selectedLang = '';

  if (currentLanguage && field[currentLanguage]) selectedLang = currentLanguage;
  else
    selectedLang = Object.keys(field).find((key) =>
      Object.values(currentLanguagesPack).includes(key)
    );

  // Перестраховываемся на случай если вообще ничего не найдет
  return field[selectedLang] ? field[selectedLang] : field[Object.keys(field)[0]];
}

function getOffsetTopWithinContainer(element: HTMLElement, container: HTMLElement): number {
  return (
    element.getBoundingClientRect().top -
    container.getBoundingClientRect().top +
    container.scrollTop
  );
}

/**
 * One-shot scroll of #layout to the URL hash target.
 * Call only when the page is ready (content rendered, not loading).
 */
export async function onScroller(hash: string) {
  const anchorId = (hash || '').replace(/^#/, '');
  if (!anchorId || typeof document === 'undefined') return;

  const targetElement = document.getElementById(anchorId);
  const layout = document.getElementById('layout');
  if (!targetElement || !layout) return;

  layout.scrollTo({
    top: getOffsetTopWithinContainer(targetElement, layout),
    left: 0,
    // @ts-expect-error: Type '"instant"' is not assignable to type 'ScrollBehavior'
    behavior: 'instant',
  });
}

//  метод для сортировки обьекта по типу,
export function orderObject(data: MethodType): OrderedObject {
  if (!data?.children || !Array.isArray(data.children)) return;

  const map: Map<string, ChildMethod[]> = data.children.reduce((acc: any, child: any) => {
    return acc.has(child.kind)
      ? acc.set(child.kind, acc.get(child.kind).concat([child]))
      : acc.set(child.kind, [child]);
  }, new Map());

  return Array.from(map).reduce((acc, [key, val]) => Object.assign(acc, { [key]: val }), {});
}

/**
 *  Вытаскиваем языки из каждого example и делаем из этого массив всех языков которые используются, и потом фильтруем их для уникального значения. (Set у меня не получилось засунуть как initial value для редьюса)
 *
 * @param tutorial - туториал
 * @return string[] из названий языков
 */
export function getLanguagesHelper(tutorial: TutorialType) {
  if (!tutorial?.content) return [];

  const reducer = (acc: any, value: any) => [
    ...acc,
    ...(value.examples ? value.examples.reduce(reducer, []) : [value.lang.toUpperCase()]),
  ];

  const unfiltredResult = tutorial.content
    .filter((obj: { kind: string }) => obj.kind === 'content_source')
    .reduce(reducer, []);

  // Фильтруем на уникальное значение через Set, а потом если есть
  return Array.from(new Set(unfiltredResult)).filter((value, i, array) =>
    value === 'NODEJS' ? !array.includes('JAVASCRIPT') : value
  );
}

/**
 * Делаем из ['1-7', '10'] [1,2,3,4,5,6,7,10]
 */
export function computeHighLightNumberHelper(highlightArrayStrings: string[]): number[] {
  if (!highlightArrayStrings) return [];

  // Фильтруем на пустые значения в массиве
  highlightArrayStrings = highlightArrayStrings.filter((value) => value !== '');

  if (!highlightArrayStrings.length) return [];

  const highlightArrayNumbers: number[] = [];

  highlightArrayStrings.forEach((numberSting: string) => {
    if (!numberSting.includes('-')) {
      highlightArrayNumbers.push(+numberSting);
      return;
    }

    const tempArray: string[] = numberSting.split('-');

    let startArray = +tempArray[0];
    const endArray = +tempArray[1];

    for (startArray; startArray <= endArray; startArray++) highlightArrayNumbers.push(startArray);
  });
  return highlightArrayNumbers;
}

export function getLocaleFromLanguageCode(languageCode: string | (string | null)[]): LocaleItem {
  const currentLocale = localeList.filter((l: LocaleItem) => l.lc == languageCode);

  return currentLocale ? currentLocale[0] : localeList[0];
}
